import * as React from "react";
import {translate} from "react-i18next";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";

import {Segment, Table} from "semantic-ui-react";

import {AssignedUserRoleDto, OptionBasicDtoOptionType, RoleDto, UserDto} from "../../../services/Client";

import Button from "../../../components/Button";
import DeleteButton from "../../../components/DeleteButton";
import DeputySelection from "./DeputySelection";
import AssignedRoleSelection, {RoleAssignmentType} from "./AssignedRoleSelection";
import UserDetailsDialog from "./UserDetailsDialog";
import {extractRoles} from "../AdminUtils";
import MultiOptionTypeSelection from "./MultiOptionTypeSelection";
import {DateFormatter} from "../../../utils/DateFormatter";

interface IOptionalControls {
  /** Check whether a given DTO has the links to get details. */
  canGetUserDetails: (dto: UserDto) => boolean;

  /** Check whether a given DTO has the links to update itself. */
  canUpdateUser: (dto: UserDto) => boolean;

  /** Check whether a given DTO has the links to delete itself. */
  canDeleteUser: (dto: UserDto) => boolean;
}

export interface IUsersTableProps extends ITranslatedComponent {
  /** list with all users */
  users: UserDto[];

  /** User objects to display in the table. */
  filteredUsers: UserDto[];

  /** All available roles that may be assigned to the users. */
  roles: RoleDto[];

  /** Whether or not the control is waiting for a response from the back end. */
  loading: boolean;

  /** Callbacks to check whether a given DTO has the links for optional features. */
  optionalControls: IOptionalControls;

  /** Callback for when the table want's to update a user. */
  onUpdateUser: (oldDto: UserDto, newDto: UserDto) => any;

  /** Callback for when the table want's to delete a user. */
  onDeleteUser: (dto: UserDto) => any;
}

interface IUsersTableState {
  /** Whether or not to show the user details dialog. */
  showDetailsDialog?: UserDto;
}

/**
 * A table-like component that displays a collection of UserDtos in a
 * structured way and offers actions to manipulate said users given
 * the necessary privileges/links.
 *
 * The component doesn't manipulate data itself but relies on the client
 * to provide the necessary call backs to update and delete a user.
 */
export class UsersTable extends React.Component<IUsersTableProps, IUsersTableState> {
  constructor(props: IUsersTableProps) {
    super(props);

    this.state = {
      showDetailsDialog: undefined
    };
  }

  renderHeader = (): JSX.Element => (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>{this.props.t("CN")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("DISPLAY_NAME")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("DEPARTMENT")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("DEPUTY")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("ASSIGNED_ROLES")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("LAST_LOGIN")}</Table.HeaderCell>
        <Table.HeaderCell>{this.props.t("ACTIONS")}</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  )

  renderUsername = (dto: UserDto): JSX.Element => (
    <Table.Cell>{dto.cn}</Table.Cell>
  )
  renderDisplayName = (dto: UserDto): JSX.Element => (
    <Table.Cell>{dto.name}</Table.Cell>
  )
  renderDepartment = (dto: UserDto): JSX.Element => (
    <Table.Cell>{dto.department}</Table.Cell>
  )

  renderDeputy = (dto: UserDto): JSX.Element => (
    <Table.Cell className="deputy-cell">
      {this.props.optionalControls.canUpdateUser(dto) ? (
          <DeputySelection
            placeholder={this.props.t("SELECT_DEPUTY")}
            // don't offer the current user as possible deputy
            users={this.props.users.filter((user) => user.cn !== dto.cn)}
            // pre-select deputy if available
            value={this.props.users.find(user => dto.deputy !== undefined && dto.deputy !== null && user.cn === dto.deputy.cn)}
            // undefined in case an existing deputy was removed
            onChange={(deputy: UserDto | undefined) => {
                const updatedDto = Object.assign({}, dto, {deputy: deputy});
                // @ts-ignore already checked
                this.props.onUpdateUser(dto, updatedDto);
            }}
          />
        ) : (
          dto.deputy && dto.deputy.cn
        )
      }

    </Table.Cell>
  )

  renderOptionTypeControl = (dto: UserDto): JSX.Element => {
    let _optionTypes: any = dto.optionTypes;
    return (
      <div>
        <b>{this.props.t("OPTION_TYPES")}: </b>
        {this.props.optionalControls.canUpdateUser(dto) ? (
            <MultiOptionTypeSelection
              placeholder={this.props.t("SELECT_OPTION_TYPES")}
              value={_optionTypes}
              onChange={(optionTypes: OptionBasicDtoOptionType[] | undefined) => {
                if (this.props.onUpdateUser) {
                  const updatedDto = dto;
                  updatedDto.optionTypes = optionTypes as any[];
                  this.props.onUpdateUser(dto, updatedDto);
                }
              }}
            />
          ) : (
            dto.optionTypes &&
            dto.optionTypes.map((oType) => this.props.t('GLOBAL:' + oType) + ' (' + oType + ')').join(', ')
          )
        }
      </div>
    );
  }

  renderAssignedRoles = (dto: UserDto): JSX.Element => (
    <div>
      <b>{this.props.t("ROLES")}: </b>
      {this.props.optionalControls.canUpdateUser(dto) ? (
          <AssignedRoleSelection
            roles={this.props.roles}
            roleAssignType={RoleAssignmentType.USER}
            parentDto={dto}
            values={dto.assignedRoles}
            onChange={(assignedRoles: AssignedUserRoleDto[]) => {
              if (this.props.onUpdateUser) {
                const updatedDto = Object.assign({}, dto, {
                  assignedRoles: assignedRoles
                });
                this.props.onUpdateUser(dto, updatedDto);
              }
            }}
          />
        ) : (
          dto.assignedRoles &&
          extractRoles(dto.assignedRoles).map((role: RoleDto) => role.name).join(", ")
        )
      }
    </div>
  )

  renderLastLogin = (dto: UserDto): JSX.Element => (
    <Table.Cell>
      {dto.lastLogin !== undefined && dto.lastLogin !== 0 && DateFormatter.applyDateFormat(new Date(dto.lastLogin))}
    </Table.Cell>
  )

  renderActions = (dto: UserDto): JSX.Element => (
    <Table.Cell>
      {this.props.optionalControls.canDeleteUser(dto) &&
        <DeleteButton
          label={this.props.t("DELETE_USER")}
          confirmLabel={this.props.t("CONFIRM")}
          disabled={this.props.loading}
          requireConfirmation={true}
          onClick={() => {
            if (this.props.onDeleteUser) {
              this.props.onDeleteUser(dto);
            }
          }}
        />
      }
      {this.props.optionalControls.canGetUserDetails(dto) &&
        <Button
          label={this.props.t("SHOW_DETAILS")}
          labelPosition="after"
          icon={"user circle"}
          onClick={() => this.setState({showDetailsDialog: dto})}
        />
      }
    </Table.Cell>
  )

  renderRows = (dtos: UserDto[]): JSX.Element[] => (
    dtos.map((dto, index) => (
      <Table.Row key={index} style={{backgroundColor: index % 2 === 0 ? 'white' : '#80808029'}}>
        {this.renderUsername(dto)}
        {this.renderDisplayName(dto)}
        {this.renderDepartment(dto)}
        {this.renderDeputy(dto)}
        <Table.Cell>
          {this.renderAssignedRoles(dto)}
          {this.renderOptionTypeControl(dto)}
        </Table.Cell>
        {this.renderLastLogin(dto)}
        {this.renderActions(dto)}
      </Table.Row>
    ))
  )

  renderTable = (dtos: UserDto[]): JSX.Element => (
    <Table celled={true}>
      {this.renderHeader()}
      <Table.Body>
        {this.renderRows(dtos)}
      </Table.Body>
    </Table>
  )

  render() {
    return (
      <Segment loading={this.props.loading}>
        {this.renderTable(this.props.filteredUsers)}

        {
          this.state.showDetailsDialog &&
          <UserDetailsDialog
            user={this.state.showDetailsDialog}
            onClose={() => {this.setState({showDetailsDialog: undefined});}}
          />
        }
      </Segment>
    );
  }
}

export default translate("ADMIN_VIEW")(UsersTable);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/UsersTable.tsx